export type SystemCapability = Components.Schemas.SystemCapability;

export type SystemCapabilityName =
	| 'userRoleManagement'
	| 'globalCompanyManagement'
	| 'companyRelationshipManagement'
	| 'propertyManagement'
	| 'deviceManagement'
	| 'transferDeviceInventoryOwnership'
	| 'issueFlexCodes'
	| 'runReports'
	| 'gdprCompliance'
	| 'mobileAccess'
	| 'billingManagement'
	| 'auditLogs'
	| 'deviceManagementView'
	| 'issueFlexCodesView'
	| 'globalCompanyManagementView';

export enum SystemCapabilityEnum {
	// In-memory codes after name→enum resolution. Prefer capability `name` over assuming
	// these equal SystemCapability.id in every environment.
	userRoleManagement = 1,
	globalCompanyManagement = 2,
	companyRelationshipManagement = 3,
	propertyManagement = 4,
	deviceManagement = 5,
	transferDeviceInventoryOwnership = 6,
	issueFlexCodes = 7,
	runReports = 8,
	gdprCompliance = 9,
	mobileAccess = 10,
	billingManagement = 11,
	auditLogs = 12,
	deviceManagementView = 13,
	issueFlexCodesView = 14,
	globalCompanyManagementView = 15,
}

export type SystemCapabilityItem = {
	systemCapability: SystemCapability;
	granted?: boolean;
};

export type SystemCapabilityDisplayItem = {
	systemCapabilityItem: SystemCapabilityItem;
	displayName: string;
	description: string;
};

export const nameToEnum = (
	scName: string
): SystemCapabilityEnum | undefined => {
	switch (scName) {
		case 'userRoleManagement':
			return SystemCapabilityEnum.userRoleManagement;
		case 'globalCompanyManagement':
			return SystemCapabilityEnum.globalCompanyManagement;
		case 'companyRelationshipManagement':
			return SystemCapabilityEnum.companyRelationshipManagement;
		case 'propertyManagement':
			return SystemCapabilityEnum.propertyManagement;
		case 'deviceManagement':
			return SystemCapabilityEnum.deviceManagement;
		case 'transferDeviceInventoryOwnership':
			return SystemCapabilityEnum.transferDeviceInventoryOwnership;
		case 'issueFlexCodes':
			return SystemCapabilityEnum.issueFlexCodes;
		case 'runReports':
			return SystemCapabilityEnum.runReports;
		case 'gdprCompliance':
			return SystemCapabilityEnum.gdprCompliance;
		case 'mobileAccess':
			return SystemCapabilityEnum.mobileAccess;
		case 'billingManagement':
			return SystemCapabilityEnum.billingManagement;
		case 'auditLogs':
			return SystemCapabilityEnum.auditLogs;
		case 'deviceManagementView':
			return SystemCapabilityEnum.deviceManagementView;
		case 'issueFlexCodesView':
			return SystemCapabilityEnum.issueFlexCodesView;
		case 'globalCompanyManagementView':
			return SystemCapabilityEnum.globalCompanyManagementView;
		default:
			return undefined;
	}
};

export const systemCapabilityToEnum = (
	systemCapability: SystemCapability
): SystemCapabilityEnum | undefined => {
	return nameToEnum(systemCapability.name);
};

const displayItemLookups: Map<
	SystemCapabilityEnum,
	{ displayName: string; description: string }
> = new Map([
	[
		SystemCapabilityEnum.userRoleManagement,
		{
			displayName: $localize`:@@systemCapability-displayNameUserRoleManagement:User & Role Management`,
			description: $localize`:@@systemCapability-newDescriptionUserRoleManagement:Responsible for managing company roles, assigning users, and deactivating/reactivating user accounts as needed.`,
		},
	],
	[
		SystemCapabilityEnum.globalCompanyManagement,
		{
			displayName: $localize`:@@systemCapability-displayNameGlobalCompanyManagement:Global Company Management`,
			description: $localize`:@@systemCapability-descriptionGlobalCompanyManagement:Full global system ability to add, edit, and remove any company in the system, including their management of their properties, devices, users, and roles.`,
		},
	],
	[
		SystemCapabilityEnum.companyRelationshipManagement,
		{
			displayName: $localize`:@@systemCapability-displayNameCompanyRelationshipManagement:Manage Company Relationships`,
			description: $localize`:@@systemCapability-descriptionCompanyRelationshipManagement:Ability to curate My Companies List.`,
		},
	],
	[
		SystemCapabilityEnum.propertyManagement,
		{
			displayName: $localize`:@@systemCapability-displayNamePropertyManagement:Property Management`,
			description: $localize`:@@systemCapability-descriptionPropertyManagement:Ability to add, edit, and remove your company's properties.`,
		},
	],
	[
		SystemCapabilityEnum.deviceManagement,
		{
			displayName: $localize`:@@systemCapability-displayNameDeviceManagement:Device Management`,
			description: $localize`:@@systemCapability-descriptionDeviceManagement:Ability to create, edit, and remove my company's device groups & device access assignments, along with manage device installations and removals.`,
		},
	],
	[
		SystemCapabilityEnum.transferDeviceInventoryOwnership,
		{
			displayName: $localize`:@@systemCapability-displayNameTransferDeviceInventoryOwnership:Transfer Device Inventory Ownership`,
			description: $localize`:@@systemCapability-descriptionTransferDeviceInventoryOwnership:Ability to change the ultimate owning or controlling company for devices.`,
		},
	],
	[
		SystemCapabilityEnum.issueFlexCodes,
		{
			displayName: $localize`:@@systemCapability-displayNameIssueCodes:Generate Codes`,
			description: $localize`:@@systemCapability-descriptionIssueCodes:Ability to generate codes for devices as needed.`,
		},
	],
	[
		SystemCapabilityEnum.runReports,
		{
			displayName: $localize`:@@systemCapability-displayNameRunReports:Reports`,
			description: $localize`:@@systemCapability-descriptionRunReports:Allows users to create and export reports on device activity, inventory status, and access history.`,
		},
	],
	[
		SystemCapabilityEnum.gdprCompliance,
		{
			displayName: $localize`:@@systemCapability-displayNameGdprCompliance:GDPR Compliance`,
			description: $localize`:@@systemCapability-descriptionGdprCompliance:Ability to de-identify users in the system.`,
		},
	],
	[
		SystemCapabilityEnum.mobileAccess,
		{
			displayName: $localize`:@@systemCapability-displayNameMobileAccess:Mobile Device Access`,
			description: $localize`:@@systemCapability-descriptionMobileAccess:For users who need to utilize the mobile app.`,
		},
	],
	[
		SystemCapabilityEnum.billingManagement,
		{
			displayName: $localize`:@@systemCapability-displayNameBillingManagement:Billing`,
			description: $localize`:@@systemCapability-descriptionBillingManagement:Ability to add, view, and end my company's billing invoices.`,
		},
	],
	[
		SystemCapabilityEnum.auditLogs,
		{
			displayName: $localize`:@@systemCapability-displayNameAuditLogs:Audit Logs`,
			description: $localize`:@@systemCapability-descriptionAuditLogs:Ability to view, search, and export audit logs tracking user actions and system events.`,
		},
	],
	[
		SystemCapabilityEnum.deviceManagementView,
		{
			displayName: $localize`:@@systemCapability-displayNameDeviceManagementView:Device Management (VIEW)`,
			description: $localize`:@@systemCapability-descriptionDeviceManagementView:Ability to view my company's device groups & device access assignments, along with view device installations and removals.`,
		},
	],
	[
		SystemCapabilityEnum.issueFlexCodesView,
		{
			displayName: $localize`:@@systemCapability-displayNameIssueCodesView:Generate Codes (VIEW)`,
			description: $localize`:@@systemCapability-descriptionIssueCodesView:Ability to view generated codes for a specific device.`,
		},
	],
	[
		SystemCapabilityEnum.globalCompanyManagementView,
		{
			displayName: $localize`:@@systemCapability-displayNameGlobalCompanyManagementView:Global Company Management (VIEW)`,
			description: $localize`:@@systemCapability-descriptionGlobalCompanyManagementView:View-only access to any company in the system, including their properties, devices, users, and roles.`,
		},
	],
]);

export const emptySystemCapabilityDisplayItem =
	(): SystemCapabilityDisplayItem => ({
		systemCapabilityItem: {
			systemCapability: { id: '', name: '', sentrilockOnly: false },
		},
		displayName: '',
		description: '',
	});

/** Parent act capability → matching View capability (stable pairs only). */
export const VIEW_CAPABILITY_PARENT_PAIRS: ReadonlyArray<{
	parent: SystemCapabilityName;
	view: SystemCapabilityName;
}> = [
	{
		parent: 'globalCompanyManagement',
		view: 'globalCompanyManagementView',
	},
	{ parent: 'deviceManagement', view: 'deviceManagementView' },
	{ parent: 'issueFlexCodes', view: 'issueFlexCodesView' },
];

const VIEW_CAPABILITY_NAMES = new Set(
	VIEW_CAPABILITY_PARENT_PAIRS.map((pair) => pair.view)
);

/**
 * Places each View capability immediately after its parent act capability.
 * Does not reorder any other rows; orphan View rows (parent missing) stay at the end.
 */
export function placeViewCapabilitiesAfterParents(
	items: SystemCapabilityDisplayItem[]
): SystemCapabilityDisplayItem[] {
	const viewByName = new Map<string, SystemCapabilityDisplayItem>();
	const withoutViews: SystemCapabilityDisplayItem[] = [];

	for (const item of items) {
		const name = item.systemCapabilityItem.systemCapability.name;
		if (VIEW_CAPABILITY_NAMES.has(name as SystemCapabilityName)) {
			viewByName.set(name, item);
		} else {
			withoutViews.push(item);
		}
	}

	if (viewByName.size === 0) {
		return items;
	}

	const result: SystemCapabilityDisplayItem[] = [];
	const placedViews = new Set<string>();

	for (const item of withoutViews) {
		result.push(item);
		const parentName = item.systemCapabilityItem.systemCapability.name;
		const pair = VIEW_CAPABILITY_PARENT_PAIRS.find(
			(p) => p.parent === parentName
		);
		if (pair === undefined) {
			continue;
		}
		const viewItem = viewByName.get(pair.view);
		if (viewItem !== undefined) {
			result.push(viewItem);
			placedViews.add(pair.view);
		}
	}

	for (const pair of VIEW_CAPABILITY_PARENT_PAIRS) {
		if (!placedViews.has(pair.view) && viewByName.has(pair.view)) {
			result.push(viewByName.get(pair.view)!);
		}
	}

	return result;
}

/**
 * Places Audit Logs immediately after Reports (and therefore above Mobile Device Access
 * when both are present). API/DB id order otherwise leaves auditLogs after mobileAccess.
 */
export function placeAuditLogsAfterReports(
	items: SystemCapabilityDisplayItem[]
): SystemCapabilityDisplayItem[] {
	const auditIndex = items.findIndex(
		(item) => item.systemCapabilityItem.systemCapability.name === 'auditLogs'
	);
	const reportsIndex = items.findIndex(
		(item) => item.systemCapabilityItem.systemCapability.name === 'runReports'
	);

	if (auditIndex < 0 || reportsIndex < 0) {
		return items;
	}

	// Already directly below Reports
	if (auditIndex === reportsIndex + 1) {
		return items;
	}

	const result = [...items];
	const [auditItem] = result.splice(auditIndex, 1);
	const insertAt = auditIndex < reportsIndex ? reportsIndex : reportsIndex + 1;
	result.splice(insertAt, 0, auditItem);
	return result;
}

/**
 * Applies capability matrix display ordering used for Oracle / Support / all roles:
 * 1) View caps immediately after their act parents
 * 2) Audit Logs immediately after Reports
 */
export function orderSystemCapabilityDisplayItems(
	items: SystemCapabilityDisplayItem[]
): SystemCapabilityDisplayItem[] {
	return placeAuditLogsAfterReports(placeViewCapabilitiesAfterParents(items));
}

export function toDisplayItem(
	systemCapabilityItem: SystemCapabilityItem,
	webHaspCodes: boolean,
	isOracle: boolean,
	companyOwnsDevices: boolean = true,
	isAlreadyGranted: boolean = false,
	companyType?: 'advanced' | 'care'
): SystemCapabilityDisplayItem {
	const enumValue = systemCapabilityToEnum(
		systemCapabilityItem.systemCapability
	);
	if (enumValue === undefined) {
		return emptySystemCapabilityDisplayItem();
	}

	// todo these are hidden for now
	switch (enumValue) {
		case SystemCapabilityEnum.transferDeviceInventoryOwnership:
		case SystemCapabilityEnum.gdprCompliance:
			return emptySystemCapabilityDisplayItem();
		case SystemCapabilityEnum.deviceManagementView:
		case SystemCapabilityEnum.issueFlexCodesView:
		case SystemCapabilityEnum.globalCompanyManagementView:
			// View capabilities are internal-only; shown via sentrilockOnly list filtering
			break;
		case SystemCapabilityEnum.billingManagement:
			if (!isOracle) {
				return emptySystemCapabilityDisplayItem();
			}
			break;
		case SystemCapabilityEnum.auditLogs:
			// Show audit logs if: user is Oracle AND company owns devices OR capability is already granted
			if (!isOracle && !companyOwnsDevices && !isAlreadyGranted) {
				return emptySystemCapabilityDisplayItem();
			}
			break;
	}

	if (!webHaspCodes && enumValue === SystemCapabilityEnum.issueFlexCodes) {
		// if the switch is off use the old value for the text
		return {
			systemCapabilityItem,
			displayName: $localize`:@@systemCapability-displayNameIssueFlexCodes:Generate Access Codes`,
			description: $localize`:@@systemCapability-descriptionIssueFlexCodes:Ability to generate access codes for devices as needed.`,
		};
	}

	// For care company types, modify property management text
	if (
		companyType === 'care' &&
		enumValue === SystemCapabilityEnum.propertyManagement
	) {
		return {
			systemCapabilityItem,
			displayName: $localize`:@@systemCapability-displayNamePropertyClientManagement:Property & Client Management`,
			description: $localize`:@@systemCapability-descriptionPropertyClientManagement:Ability to add, edit, and remove your company's properties and manage client information.`,
		};
	}

	// tslint:disable-next-line:no-non-null-assertion
	const { displayName, description } = displayItemLookups.get(enumValue)!;
	return { systemCapabilityItem, displayName, description };
}
